export type MediaType = "image" | "video";

const ALLOWED_CONTENT_TYPES: Record<string, MediaType> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/gif": "image",
  "image/webp": "image",
  "video/mp4": "video",
  "video/quicktime": "video",
};

const ALLOWED_EXTENSIONS: Record<string, MediaType> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  mp4: "video",
  mov: "video",
};

const ALLOWED_LABEL = "JPG, PNG, GIF, WebP, MP4, or MOV";

const MAX_FILE_SIZE_VIDEO = 50 * 1024 * 1024;
const MAX_FILE_SIZE_IMAGE = 5 * 1024 * 1024;

const PROBE_TIMEOUT_MS = 8_000;

function mediaTypeFromExtension(url: URL): MediaType | undefined {
  const extension = url.pathname.split(".").pop()?.toLowerCase();
  return extension && Object.hasOwn(ALLOWED_EXTENSIONS, extension) ? ALLOWED_EXTENSIONS[extension] : undefined;
}

function probeMediaElementLoad(url: URL, mediaType: MediaType): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false;
    const settle = (loaded: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      resolve(loaded);
    };

    const timeoutId = setTimeout(() => settle(false), PROBE_TIMEOUT_MS);

    if (mediaType === "video") {
      const video = document.createElement("video");
      video.addEventListener('loadedmetadata', () => settle(true));
      video.onerror = () => settle(false);
      video.src = url.href;
    } else {
      const image = new Image();
      image.addEventListener('load', () => settle(true));
      image.onerror = () => settle(false);
      image.src = url.href;
    }
  });
}

async function probeMediaType(url: URL): Promise<MediaType | undefined> {
  const hintedType = mediaTypeFromExtension(url);
  const candidates: MediaType[] = hintedType === "video" ? ["video", "image"] : ["image", "video"];

  for (const candidate of candidates) {
    if (await probeMediaElementLoad(url, candidate)) {
      return candidate;
    }
  }

  return undefined;
}

// Validates a media URL by making a HEAD request to check Content-Type and file size.
// Some servers respond to HEAD with an error page (405/404) or a generic Content-Type
// like application/octet-stream, so a missing/unrecognized Content-Type falls back to
// the URL's file extension instead of rejecting a genuinely valid file.

// Returns an error message if the type is unsupported or the file exceeds the size limit
// (5MB for images, 50MB for video). Returns the detected mediaType on success.

// If the HEAD request itself fails (CORS or network error), falls back to actually trying
// to load the URL as media — file size can't be verified in that case, since a CORS
// failure also blocks reading the content-length header.
export async function validateMediaUrl(url: URL): Promise<{ error?: string; mediaType?: MediaType }> {
  if (url.protocol !== "https:") {
    return { error: "URL must use HTTPS" };
  }

  try {
    const res = await fetch(url.href, { method: "HEAD" });
    const contentType = res.headers.get("content-type")?.split(";")[0].trim().toLowerCase() ?? "";
    const mediaType = Object.hasOwn(ALLOWED_CONTENT_TYPES, contentType)
      ? ALLOWED_CONTENT_TYPES[contentType]
      : mediaTypeFromExtension(url);

    if (!mediaType) {
      return { error: `File must be a ${ALLOWED_LABEL}` };
    }

    const maxBytes = mediaType === "video" ? MAX_FILE_SIZE_VIDEO : MAX_FILE_SIZE_IMAGE;
    const maxLabel = mediaType === "video" ? "50MB" : "5MB";
    // A non-2xx response's content-length describes its error body, not the asset.
    const contentLength = res.ok ? res.headers.get("content-length") : null;

    if (contentLength && parseInt(contentLength, 10) > maxBytes) {
      return { error: `File must be under ${maxLabel}` };
    }

    return { mediaType };
  } catch {
    const mediaType = await probeMediaType(url);

    return mediaType
      ? { mediaType }
      : { error: "Could not load this URL as media. Check that the URL is correct and publicly accessible." };
  }
}

// Parses a raw string and runs it through validateMediaUrl, surfacing a
// friendly error for malformed input instead of throwing.
export async function checkMediaUrl(rawUrl: string): Promise<{ error?: string; mediaType?: MediaType }> {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(rawUrl);
  } catch {
    return { error: "Please enter a valid URL" };
  }

  if (!/^https:\/\//i.test(rawUrl.trim())) {
    return { error: "URL must use HTTPS" };
  }

  return validateMediaUrl(parsedUrl);
}
